import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BuildingFilterChips = ({ topics, selected, toggleTopic }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    const updateScrollButtons = () => {
        if (!scrollRef.current) return;

        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

        setShowLeft(scrollLeft > 5);
        setShowRight(scrollLeft + clientWidth < scrollWidth - 5);
    };

    useEffect(() => {
        updateScrollButtons();
    }, [topics]);

    const scroll = (dir: "left" | "right") => {
        if (!scrollRef.current) return;

        scrollRef.current.scrollBy({
            left: dir === "left" ? -300 : 300,
            behavior: "smooth",
        });

        // wait for smooth scroll to finish
        setTimeout(updateScrollButtons, 300);
    };

    return (
        <>
            {/* Mobile */}
            <div className="md:hidden w-full overflow-x-auto whitespace-nowrap space-x-3 p-2 scrollbar-hide">
                {topics.map((topic) => {
                    const isSelected = selected.includes(topic);
                    return (
                        <button
                            key={topic}
                            onClick={() => toggleTopic(topic)}
                            className={`px-4 py-1.5 rounded-full font-semibold border flex-shrink-0
              ${isSelected
                                    ? "bg-black text-white border-black"
                                    : "bg-white border-gray-300"
                                }`}
                        >
                            {topic}
                        </button>
                    );
                })}
            </div>

            {/* Desktop */}
            <div className="hidden md:block relative w-full min-w-0 py-1">
                {/* Left Button */}
                {showLeft && (
                    <button
                        onClick={() => scroll("left")}
                        className="absolute left-1 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-300 rounded-full shadow-md w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition"
                    >
                        <ChevronLeft className="h-7 w-7 text-gray-700" />
                    </button>
                )}

                {/* Right Button */}
                {showRight && (
                    <button
                        onClick={() => scroll("right")}
                        className="absolute right-1 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-300 rounded-full shadow-md w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition"
                    >
                        <ChevronRight className="h-9 w-9 text-gray-700" />
                    </button>
                )}

                <div
                    ref={scrollRef}
                    onScroll={updateScrollButtons}
                    className="flex gap-3 overflow-x-auto whitespace-nowrap py-2 scrollbar-hide w-full items-center scroll-smooth"
                >
                    {topics.map((topic) => {
                        const isSelected = selected.includes(topic);

                        return (
                            <button
                                key={topic}
                                onClick={() => toggleTopic(topic)}
                                className={`flex-shrink-0 px-4 py-1.5 rounded-full font-semibold border transition
                ${isSelected
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                    }`}
                            >
                                {topic}
                            </button>
                        );
                    })}
                </div>

                {/* Right Gradient */}
                {showRight && (
                    <div className="pointer-events-none absolute top-0 right-0 h-full w-20 bg-gradient-to-l from-gray-50 via-gray-50/80 to-transparent" />
                )}

                {/* Left Gradient */}
                {showLeft && (
                    <div className="pointer-events-none absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-gray-50 via-gray-50/80 to-transparent" />
                )}
            </div>
        </>
    );
};

export default BuildingFilterChips;